/** RFC 0279 S3: publishing an installed app to the registry (S2). */
import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { readDocumentContent } from '../documents/reads'
import type { IconSpec } from './app-icon'
import { type PackageRelease, releaseFromApp } from './release'
import type { AppDoc, SourceManifest } from './types'

/** The registry there is. Production gets its own once one is deployed. */
export const DEFAULT_REGISTRY_URL =
  'https://mod-shelf-staging.mod-workers.workers.dev'

export interface Published {
  releaseId: string
  lineageId: string
  version: string
  publishedAt: number
}

/** One package as the registry lists it: its newest live version's page. */
export interface RegistryEntry {
  lineageId: string
  name: string
  description: string
  kind: string
  category: string
  about: string
  screenshots: string[]
  publisher: { id: string; name: string }
  latest: { version: string; releaseId: string; publishedAt: number }
  /** The newest live version's tile. Absent on what was published before the
   *  registry kept icons (it answers null for those). */
  icon?: IconSpec | null
}

export interface RegistryPackage extends RegistryEntry {
  versions: {
    version: string
    releaseId: string
    publishedAt: number
    size: number
  }[]
}

/**
 * The files a package was built from (RFC 0279 S9), published beside a
 * release and fetched only to fork it. Paths are project-relative.
 */
export interface SourceBundle {
  files: { path: string; content: string }[]
  /** What rebuilds the view from these files, from the app's manifest. */
  build?: string
  /** What the build emits, project-relative. */
  entry?: string
}

export interface Registry {
  publish(release: PackageRelease): Promise<Published>
  /** Store a source bundle and return the id that names its bytes. */
  uploadSource(bundle: SourceBundle): Promise<string>
  /** A source bundle's bytes, checked against the id that names them. */
  source(sourceId: string): Promise<SourceBundle>
  /**
   * Packages matching `query` in name, description or about, and in `category`
   * when given; all when both are absent. The registry filters before it limits,
   * so a search reaches packages older than the newest page.
   */
  list(query?: string, category?: string): Promise<RegistryEntry[]>
  featured(): Promise<RegistryEntry[]>
  package(lineageId: string): Promise<RegistryPackage>
  /** One release's bytes, checked against the id that names them. */
  release(releaseId: string): Promise<PackageRelease>
}

const RELEASE_ID = /^[0-9a-f]{64}$/

export function isReleaseId(value: string): boolean {
  return RELEASE_ID.test(value)
}

async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(text),
  )
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/** A registry over HTTP. The token is asked for on every call, because the one
 *  on disk is short-lived and replaced under a running process. */
export function registryClient(opts: {
  baseUrl?: string
  getAuthToken: () => string | Promise<string>
  fetch?: typeof fetch
}): Registry {
  const base = (opts.baseUrl ?? DEFAULT_REGISTRY_URL).replace(/\/+$/, '')
  const send = opts.fetch ?? fetch
  return {
    async list(query, category) {
      const params = new URLSearchParams()
      if (query?.trim()) params.set('q', query.trim())
      if (category) params.set('category', category)
      const q = params.size ? `?${params}` : ''
      const res = await send(`${base}/packages${q}`)
      if (!res.ok)
        throw new Error(`The registry did not answer (${res.status})`)
      return ((await res.json()) as { packages: RegistryEntry[] }).packages
    },
    async featured() {
      const res = await send(`${base}/featured`)
      if (!res.ok)
        throw new Error(`The registry did not answer (${res.status})`)
      return ((await res.json()) as { packages: RegistryEntry[] }).packages
    },
    async package(lineageId) {
      const res = await send(
        `${base}/packages/${encodeURIComponent(lineageId)}`,
      )
      if (!res.ok) throw new Error(`No package ${lineageId} (${res.status})`)
      return (await res.json()) as RegistryPackage
    },
    async release(releaseId) {
      if (!isReleaseId(releaseId)) {
        throw new Error(`"${releaseId}" is not a release id`)
      }
      const res = await send(`${base}/releases/${releaseId}`)
      if (res.status === 410) throw new Error('That release was withdrawn')
      if (!res.ok) throw new Error(`No release ${releaseId} (${res.status})`)
      const text = await res.text()
      // The id is the hash of the bytes, so what arrived is what was published.
      if ((await sha256Hex(text)) !== releaseId) {
        throw new Error(`Release ${releaseId} does not match its id`)
      }
      return JSON.parse(text) as PackageRelease
    },
    async uploadSource(bundle) {
      const token = (await opts.getAuthToken()).trim()
      if (!token) {
        throw new Error('Publishing needs a Mod sign-in: run `mod auth login`')
      }
      const res = await send(`${base}/sources`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bundle),
      })
      const body = (await res.json().catch(() => ({}))) as {
        sourceId?: string
        error?: string
      }
      if (!res.ok || !body.sourceId) {
        throw new Error(
          `The registry refused the source: ${body.error ?? res.statusText} (${res.status})`,
        )
      }
      return body.sourceId
    },
    async source(sourceId) {
      if (!isReleaseId(sourceId)) {
        throw new Error(`"${sourceId}" is not a source id`)
      }
      const res = await send(`${base}/sources/${sourceId}`)
      if (!res.ok) throw new Error(`No source ${sourceId} (${res.status})`)
      const text = await res.text()
      if ((await sha256Hex(text)) !== sourceId) {
        throw new Error(`Source ${sourceId} does not match its id`)
      }
      return JSON.parse(text) as SourceBundle
    },
    async publish(release) {
      const token = (await opts.getAuthToken()).trim()
      if (!token) {
        throw new Error('Publishing needs a Mod sign-in: run `mod auth login`')
      }
      const res = await send(`${base}/packages`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(release),
      })
      const body = (await res.json().catch(() => ({}))) as Partial<
        Published & { error: string }
      >
      if (!res.ok) {
        throw new Error(
          `The registry refused ${release.name} ${release.version}: ` +
            `${body.error ?? res.statusText} (${res.status})`,
        )
      }
      return body as Published
    },
  }
}

const SEMVER = /^(\d+)\.(\d+)\.(\d+)$/

/** The version after `version`: a patch bump. */
export function nextVersion(version: string): string {
  const m = SEMVER.exec(version)
  if (!m) throw new Error(`Cannot bump version "${version}"`)
  return `${m[1]}.${m[2]}.${Number(m[3]) + 1}`
}

/** A lineage id for a package's first publish: its name, made safe, and a
 *  random tail, since the first account to publish a lineage owns it. */
export function newLineageId(name: string): string {
  const slug =
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'package'
  const tail = [...crypto.getRandomValues(new Uint8Array(3))]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
  return `${slug}-${tail}`
}

export interface PublishAppOptions {
  /** The catalog the app is installed in: a project or the World. */
  catalogId: string
  appId: string
  registry: Registry
  page: { category: string; about?: string; screenshots?: string[] }
  /** Defaults to the lineage it was last published as, or a new one. */
  lineageId?: string
  /** Defaults to a patch bump of the last publish; for a lineage named here
   *  that it was not last published as, a bump of the registry's newest; or
   *  1.0.0. */
  version?: string
  /** Publish the app's source with it, for forking (RFC 0279 3c). Absent
   *  means the default: included for an app an agent wrote in a
   *  conversation, left out for a pushed folder. `true` is refused for an
   *  app that has no source. */
  includeSource?: boolean
}

/**
 * Where an app's source comes from (RFC 0279 3c), which decides whether a
 * publish includes it by default.
 *
 * `folder`: pushed by `app push`, which recorded its files in a manifest.
 * `conversation`: written in place by an agent. Nothing was built, so the view
 * and skill it wrote ARE the source. `null`: a view that is build output with
 * no recorded files, such as one installed from the registry. Source is never
 * inferred from build output.
 */
export async function sourceOrigin(
  repo: Repo,
  appId: string,
): Promise<'folder' | 'conversation' | null> {
  const app = await ready<AppDoc>(repo, appId)
  if (!app) return null
  if (app.sourceDocId) return 'folder'
  if (!app.viewDocId || !app.skillDocId) return null
  const view = await ready<{ generated?: boolean }>(repo, app.viewDocId)
  return view && !view.generated ? 'conversation' : null
}

/**
 * An app's source as a bundle a fork can push again.
 *
 * A pushed app gives the files its manifest recorded (RFC 0274). An app an
 * agent wrote gives its view and skill, with a `package.json` whose build is
 * a no-op and whose entry is the view, so `app push` from a fork registers
 * the same app under a new id. Null when there is no source.
 */
export async function sourceBundleFromApp(
  repo: Repo,
  appId: string,
): Promise<SourceBundle | null> {
  const origin = await sourceOrigin(repo, appId)
  const app = await ready<AppDoc>(repo, appId)
  if (!origin || !app) return null
  if (origin === 'conversation') {
    const viewDoc = await ready<{ mime?: string }>(repo, String(app.viewDocId))
    const skillDoc = await ready<unknown>(repo, String(app.skillDocId))
    const mime = String(viewDoc?.mime ?? 'text/html')
    const viewFile = /javascript/.test(mime) ? 'view.js' : 'view.html'
    const slug =
      String(app.name ?? 'app')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'app'
    const schema = (app.schema ?? {}) as {
      fields?: Record<string, string>
      immutable?: string[]
    }
    const mod = {
      name: String(app.name ?? slug),
      description: String(app.description ?? ''),
      dataType: String(app.dataType ?? ''),
      // Always written: `app push` reads a missing one as "document", and an
      // absent field here means "application" (RFC 0271).
      presentation: app.presentation ?? 'application',
      ...(app.handles?.length ? { handles: [...app.handles] } : {}),
      ...(app.realm ? { realm: app.realm } : {}),
      ...(app.sync ? { sync: app.sync } : {}),
      reads: [...(app.capabilities?.reads ?? [])],
      writes: [...(app.capabilities?.writes ?? [])],
      fields: { ...(schema.fields ?? {}) },
      ...(schema.immutable?.length ? { immutable: [...schema.immutable] } : {}),
      // Nothing to build: the view is the source. `true` exits 0 and leaves
      // the entry where it is.
      build: 'true',
      entry: viewFile,
      source: [viewFile],
    }
    return {
      files: [
        {
          path: 'package.json',
          content: `${JSON.stringify(
            { name: slug, version: '0.1.0', private: true, mod },
            null,
            2,
          )}\n`,
        },
        { path: viewFile, content: readDocumentContent(viewDoc) },
        { path: 'SKILL.md', content: readDocumentContent(skillDoc) },
      ],
      build: 'true',
      entry: viewFile,
    }
  }
  const manifestDoc = await ready<unknown>(repo, String(app.sourceDocId))
  const manifest = JSON.parse(
    readDocumentContent(manifestDoc) || '{}',
  ) as Partial<SourceManifest>
  const files: SourceBundle['files'] = []
  for (const { path, id } of manifest.docs ?? []) {
    files.push({
      path: String(path),
      content: readDocumentContent(await ready<unknown>(repo, String(id))),
    })
  }
  if (files.length === 0) return null
  // The skill is its own document (app.skillDocId), kept out of the manifest,
  // so a fork without it would push an app with no skill.
  if (app.skillDocId && !files.some((f) => f.path === 'SKILL.md')) {
    const skill = await ready<unknown>(repo, String(app.skillDocId))
    if (skill)
      files.push({ path: 'SKILL.md', content: readDocumentContent(skill) })
  }
  return {
    files,
    ...(manifest.build ? { build: String(manifest.build) } : {}),
    ...(manifest.entry ? { entry: String(manifest.entry) } : {}),
  }
}

async function ready<T>(repo: Repo, id: string): Promise<T | undefined> {
  const handle = await repo.find<T>(id as AnyDocumentId)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
  return handle.doc() as T | undefined
}

/**
 * The source to fork a package from: its newest release's bundle.
 *
 * Installing never calls this. A release without source is still a package;
 * it just cannot be forked, and the error says so rather than returning empty.
 */
export async function forkSource(
  registry: Registry,
  lineageId: string,
): Promise<{ release: PackageRelease; bundle: SourceBundle }> {
  const item = await registry.package(lineageId)
  const release = await registry.release(item.latest.releaseId)
  if (!release.sourceReleaseId) {
    throw new Error(
      `${release.name} ${release.version} was published without source, so it cannot be forked`,
    )
  }
  return { release, bundle: await registry.source(release.sourceReleaseId) }
}

/** A lineage this app was not last published as may already have versions
 *  from another copy: bump the registry's newest, or start at 1.0.0. */
async function versionAfterRegistry(
  registry: Registry,
  lineageId: string,
): Promise<string> {
  try {
    return nextVersion((await registry.package(lineageId)).latest.version)
  } catch {
    return '1.0.0'
  }
}

/**
 * Publish an installed app: copy it out as a release, post it, and remember
 * the lineage on the app so the next publish adds a version.
 */
export async function publishApp(
  repo: Repo,
  opts: PublishAppOptions,
): Promise<Published> {
  const handle = await repo.find<AppDoc>(opts.appId as AnyDocumentId)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
  const app = handle.doc()
  if (!app) throw new Error(`App ${opts.appId} is missing`)
  const last = app.publishedAs
  const lineageId =
    opts.lineageId ?? last?.lineageId ?? newLineageId(String(app.name))
  const version =
    opts.version ??
    (last && last.lineageId === lineageId
      ? nextVersion(String(last.version))
      : opts.lineageId
        ? await versionAfterRegistry(opts.registry, lineageId)
        : '1.0.0')
  if (!opts.page.category?.trim()) {
    throw new Error('A release needs a category for its page')
  }
  const release = await releaseFromApp(repo, opts.catalogId, opts.appId, {
    lineageId,
    version,
    page: {
      category: opts.page.category.trim(),
      about: opts.page.about ?? String(app.description ?? ''),
      screenshots: opts.page.screenshots ?? [],
    },
    // The registry writes who published it, from the sign-in, not from this.
    publisher: { id: '', name: '' },
  })
  const includeSource =
    opts.includeSource ??
    (await sourceOrigin(repo, opts.appId)) === 'conversation'
  if (includeSource) {
    const bundle = await sourceBundleFromApp(repo, opts.appId)
    if (!bundle) {
      throw new Error(
        `${app.name} has no source to include: its view is build output with no recorded files`,
      )
    }
    release.sourceReleaseId = await opts.registry.uploadSource(bundle)
  }
  const published = await opts.registry.publish(release)
  handle.change((d) => {
    d.publishedAs = {
      lineageId: published.lineageId,
      version: published.version,
      releaseId: published.releaseId,
    }
  })
  return published
}
